import { html, render } from 'lit-html'
import workerURL from 'omt:../worker.js'
import { currentUser } from '../auth.js'
import { writePomodoro } from '../db.js'
import { assertNotNull, displayTimeRemaining } from '../utils.js'
import styles from './pomo-tracker.css'
import type { User } from '../types.js'

const SINGLE_POMODORO_TIME_SHORT =
  Number(process.env.POMO_DURATION_SHORT_MINUTES) * 60
const SINGLE_POMODORO_TIME_LONG =
  Number(process.env.POMO_DURATION_LONG_MINUTES) * 60

interface CurrentPomodoro {
  secondsRemaining: number
  isPaused?: boolean
}

export class PomoTracker extends HTMLElement {
  #pomodoroTime: number
  #currentPom: CurrentPomodoro | null = null
  #sound: HTMLAudioElement
  #worker: Worker | null = null
  #showingSummaryModal: boolean = false
  #showingManualModal: boolean = false
  #currentUser: User | null = null
  #unsubscribeAuth: (() => void) | null = null
  #shadow: ShadowRoot
  #onWorkerMessageBound: (event: MessageEvent) => void
  #onUnloadBound: (event: BeforeUnloadEvent) => void

  constructor() {
    super()
    this.#shadow = this.attachShadow({ mode: 'open' })

    const pomoTimeFromStorage = localStorage.getItem('__POMODONE_TIME')
    this.#pomodoroTime = pomoTimeFromStorage
      ? Number(pomoTimeFromStorage)
      : SINGLE_POMODORO_TIME_SHORT

    this.#sound = new Audio('/Gift.mp3')
    this.#sound.loop = true

    this.#onWorkerMessageBound = this.#onWorkerMessage.bind(this)
    this.#onUnloadBound = this.#onUnload.bind(this)
  }

  connectedCallback(): void {
    this.#worker = new Worker(workerURL, { name: 'counter-worker-svelte' })
    this.#worker.addEventListener('message', this.#onWorkerMessageBound)
    this.#worker.postMessage(['setPomodoroTime', this.#pomodoroTime])

    this.#unsubscribeAuth = currentUser.subscribe((user) => {
      this.#currentUser = user
      this.render()
    })

    window.addEventListener('beforeunload', this.#onUnloadBound)
    this.render()
  }

  disconnectedCallback(): void {
    if (this.#worker) {
      this.#worker.removeEventListener('message', this.#onWorkerMessageBound)
      this.#worker.terminate()
    }
    if (this.#unsubscribeAuth) {
      this.#unsubscribeAuth()
    }
    window.removeEventListener('beforeunload', this.#onUnloadBound)
  }

  #onWorkerMessage(event: MessageEvent): void {
    if (event.data.name === 'tick') {
      assertNotNull(this.#currentPom, 'Current pomodoro should not be null')
      this.#currentPom = {
        secondsRemaining: event.data.counter,
      }
    } else if (event.data.name === 'start') {
      this.#currentPom = {
        secondsRemaining: this.#pomodoroTime,
      }
    } else if (event.data.name === 'finish') {
      this.#currentPom = {
        secondsRemaining: 0,
      }
      this.#finishPomodoro()
    } else if (event.data.name === 'cancel') {
      this.#currentPom = null
    } else if (event.data.name === 'pause') {
      assertNotNull(this.#currentPom, 'Current pomodoro should not be null')
      this.#currentPom = {
        ...this.#currentPom,
        isPaused: true,
      }
    } else if (event.data.name === 'resume') {
      assertNotNull(this.#currentPom, 'Current pomodoro should not be null')
      this.#currentPom = {
        ...this.#currentPom,
        isPaused: false,
      }
    }
    this.render()
  }

  #onUnload(event: BeforeUnloadEvent): void {
    if (this.#currentPom) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  #finishPomodoro(): void {
    this.#sound.play().then(() => {
      const waitForBell = () => {
        if (!this.#sound.paused) {
          window.setTimeout(() => {
            this.#sound.pause()
            this.#sound.currentTime = 0
            waitForBell()
          }, 5000)
          return
        }

        this.#showingSummaryModal = true
        this.render()
      }

      waitForBell()
    })
  }

  #onStartPom(): void {
    if (this.#currentPom && this.#currentPom.secondsRemaining === 0) {
      return
    }
    if (!this.#currentPom) {
      if (!currentUser.get()) {
        window.alert(
          'You are not signed in. You can still use Pomodone to track your time but your pomodoros will not be saved.'
        )
      }
    }
    this.#worker?.postMessage('startTimer')
  }

  #onPausePom(): void {
    this.#worker?.postMessage('pauseOrResumeTimer')
  }

  #onCancelPom(): void {
    const shouldCancel = window.confirm(
      'Are you sure you want to cancel this pomodoro?'
    )
    if (shouldCancel) {
      this.#worker?.postMessage('cancelTimer')
    }
  }

  #onRestartPom(): void {
    const shouldRestart = window.confirm(
      'Are you sure you want to restart this pomodoro?'
    )
    if (shouldRestart) {
      this.#worker?.postMessage('restartTimer')
    }
  }

  #onTimeChange(newTime: number): void {
    this.#pomodoroTime = newTime
    if (this.#worker) {
      this.#worker.postMessage(['setPomodoroTime', this.#pomodoroTime])
      localStorage.setItem('__POMODONE_TIME', String(this.#pomodoroTime))
    }
    this.render()
  }

  #onCompletePom(summary?: string, deskOption?: 'standing' | 'walkingPad'): void {
    const user = currentUser.get()
    if (user) {
      writePomodoro(
        user.uid,
        this.#pomodoroTime,
        summary,
        'TIMER',
        deskOption === 'standing' || deskOption === 'walkingPad',
        deskOption === 'walkingPad'
      )
    }
    this.#showingSummaryModal = false
    this.#currentPom = null
    this.render()
  }

  #onShowManualModal(): void {
    const user = currentUser.get()
    if (!user) {
      window.alert('You must be signed in to log a manual Pomodoro.')
      return
    }
    this.#showingManualModal = true
    this.render()
  }

  #onCompleteManualPom(summary?: string, deskOption?: 'standing' | 'walkingPad'): void {
    const user = currentUser.get()
    if (user) {
      writePomodoro(
        user.uid,
        0,
        summary,
        'MANUAL',
        deskOption === 'standing' || deskOption === 'walkingPad',
        deskOption === 'walkingPad'
      )
    }
    this.#showingManualModal = false
    this.render()
  }

  render(): void {
    const template = html`
      <style>
        ${styles}
      </style>
      <div>
        <div class="time-select">
          <label
            class="${this.#currentPom &&
            this.#pomodoroTime === SINGLE_POMODORO_TIME_SHORT
              ? 'active'
              : ''}"
          >
            <input
              type="radio"
              ?disabled=${this.#currentPom}
              .checked=${this.#pomodoroTime === SINGLE_POMODORO_TIME_SHORT}
              @change=${() => this.#onTimeChange(SINGLE_POMODORO_TIME_SHORT)}
              name="pomDuration"
              value=${SINGLE_POMODORO_TIME_SHORT}
            />
            <span>25 minutes</span>
          </label>
          <label
            class="${this.#currentPom &&
            this.#pomodoroTime === SINGLE_POMODORO_TIME_LONG
              ? 'active'
              : ''}"
          >
            <input
              type="radio"
              ?disabled=${this.#currentPom}
              .checked=${this.#pomodoroTime === SINGLE_POMODORO_TIME_LONG}
              @change=${() => this.#onTimeChange(SINGLE_POMODORO_TIME_LONG)}
              name="pomDuration"
              value=${SINGLE_POMODORO_TIME_LONG}
            />
            <span>50 minutes</span>
          </label>
        </div>

        <div>
          <button
            class="timer-button"
            ?disabled=${this.#currentPom &&
            this.#currentPom.secondsRemaining === 0}
            @click=${() => this.#onStartPom()}
          >
            ${this.#currentPom
              ? this.#currentPom.secondsRemaining === 0
                ? 'Done'
                : displayTimeRemaining(this.#currentPom.secondsRemaining)
              : 'Start'}
          </button>
          ${!this.#currentPom && this.#currentUser
            ? html`
                <button
                  class="manual-button"
                  @click=${() => this.#onShowManualModal()}
                >
                  Log Manual Pomodoro
                </button>
              `
            : ''}
        </div>

        ${this.#currentPom && this.#currentPom.secondsRemaining > 1
          ? html`
              <div class="actions">
                <button
                  class="action-button"
                  @click=${() => this.#onPausePom()}
                >
                  ${this.#currentPom.isPaused ? 'Resume' : 'Pause'}
                </button>
                <button
                  class="action-button"
                  @click=${() => this.#onCancelPom()}
                >
                  Cancel
                </button>
                <button
                  class="action-button"
                  @click=${() => this.#onRestartPom()}
                >
                  Restart
                </button>
              </div>
            `
          : ''}
        ${this.#showingSummaryModal
          ? html`
              <div class="modal-overlay">
                <div class="summary-modal">
                  <h3>Pomodoro Completed!</h3>
                  <p>What were you working on?</p>
                  <textarea
                    id="summary-textarea"
                    placeholder="Optional: Describe what you worked on..."
                    rows="4"
                    @keydown=${(e: KeyboardEvent) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault()
                        const textarea = this.#shadow.getElementById(
                          'summary-textarea'
                        ) as HTMLTextAreaElement
                        const radio = this.#shadow.querySelector(
                          'input[name="desk-type"]:checked'
                        ) as HTMLInputElement | null
                        this.#onCompletePom(
                          textarea.value.trim() || undefined,
                          (radio?.value as 'standing' | 'walkingPad') || undefined
                        )
                      }
                    }}
                  ></textarea>
                  <div class="standing-desk-checkbox">
                    <label>
                      <input type="radio" name="desk-type" value="standing" />
                      Done at standing desk
                    </label>
                    <label>
                      <input type="radio" name="desk-type" value="walkingPad" />
                      Done at walking pad
                    </label>
                  </div>
                  <div class="modal-actions">
                    <button
                      class="save-button"
                      @click=${() => {
                        const textarea = this.#shadow.getElementById(
                          'summary-textarea'
                        ) as HTMLTextAreaElement
                        const radio = this.#shadow.querySelector(
                          'input[name="desk-type"]:checked'
                        ) as HTMLInputElement | null
                        this.#onCompletePom(
                          textarea.value.trim() || undefined,
                          (radio?.value as 'standing' | 'walkingPad') || undefined
                        )
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            `
          : ''}
        ${this.#showingManualModal
          ? html`
              <div class="modal-overlay">
                <div class="summary-modal">
                  <h3>Log Manual Pomodoro</h3>
                  <p>What did you work on?</p>
                  <textarea
                    id="manual-summary-textarea"
                    placeholder="Optional: Describe what you worked on..."
                    rows="4"
                    @keydown=${(e: KeyboardEvent) => {
                      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                        e.preventDefault()
                        const textarea = this.#shadow.getElementById(
                          'manual-summary-textarea'
                        ) as HTMLTextAreaElement
                        const radio = this.#shadow.querySelector(
                          'input[name="manual-desk-type"]:checked'
                        ) as HTMLInputElement | null
                        this.#onCompleteManualPom(
                          textarea.value.trim() || undefined,
                          (radio?.value as 'standing' | 'walkingPad') || undefined
                        )
                      }
                    }}
                  ></textarea>
                  <div class="standing-desk-checkbox">
                    <label>
                      <input type="radio" name="manual-desk-type" value="standing" />
                      Done at standing desk
                    </label>
                    <label>
                      <input type="radio" name="manual-desk-type" value="walkingPad" />
                      Done at walking pad
                    </label>
                  </div>
                  <div class="modal-actions">
                    <button
                      class="save-button"
                      @click=${() => {
                        const textarea = this.#shadow.getElementById(
                          'manual-summary-textarea'
                        ) as HTMLTextAreaElement
                        const radio = this.#shadow.querySelector(
                          'input[name="manual-desk-type"]:checked'
                        ) as HTMLInputElement | null
                        this.#onCompleteManualPom(
                          textarea.value.trim() || undefined,
                          (radio?.value as 'standing' | 'walkingPad') || undefined
                        )
                      }}
                    >
                      Add Manual Pomodoro
                    </button>
                    <button
                      class="cancel-button"
                      @click=${() => {
                        this.#showingManualModal = false
                        this.render()
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            `
          : ''}
      </div>
    `
    render(template, this.#shadow, { host: this })
  }
}

customElements.define('pomo-tracker', PomoTracker)
